import React, { useRef, useState } from 'react';
import { Box, Button, Text } from '@chakra-ui/react';
import { useStripe, useElements, PaymentElement, Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import apiClient from '../lib/api/apiClient';
import { useUserContext } from '../contexts/UserContext';
import { useUserStore } from '../store/userStore';
import { useMyLeagueStore } from '../store/myLeagueStore';
import { CartItem } from '../store/shoppingCartStore';
import { STRIPE_KEY } from '../constants';

interface PaymentFormProps {
  totalAmount?: number;
  items?: CartItem[];
}

const PaymentForm: React.FC<PaymentFormProps> = ({ items }) => {
  const formRef = useRef<HTMLFormElement>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const { myTeam, currentLeague } = useUserContext();
  const { user } = useUserStore();
  const { myLeague } = useMyLeagueStore();

  // Create payment intent payload similar to QuickPurchaseModal
  const createPaymentIntentPayload = () => {
    if (!items || items.length === 0) {
      return null;
    }

    const currency = items[0]?.item?.currency || 'USD';

    const itemsPayload = items.map((cartItem) => {
      const item = cartItem.item;
      const amountInMinorUnits = Math.round(item.price * 100 * cartItem.quantity); // Convert to cents and multiply by quantity
      
      // Build metadata similar to QuickPurchaseModal
      const itemMetadata = (item.metadata ?? {}) as Record<string, unknown>;
      console.log('itemMetadata', itemMetadata);
      let metadata: Record<string, unknown> = {
        ...itemMetadata,
        userId: user?.id,
        leagueId: myLeague?.id,
        teamId: myTeam,
        item: item.category,
        transaction: 'salary_cap'
      };


      return {
        id: item.id,
        name: item.name,
        amount: amountInMinorUnits,
        currency,
        metadata,
        quantity: cartItem.quantity,
      };
    });

    return {
      items: itemsPayload,
    };
  };



  const handlePayNow = async () => {
    if (!items || items.length === 0) {
      setMessage("No items in cart.");
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const payload = createPaymentIntentPayload();
      if (!payload) {
        setMessage('No items to process');
        setLoading(false);
        return;
      }
      console.log('payload', payload);
      const response = await apiClient.post("/payment/create-payment-intent", payload);
      console.log('response', response);
      
      const { clientSecret, checkoutUrl } = response.data ?? {};

      if (checkoutUrl) {
        // Redirect to Stripe Checkout
        window.location.href = checkoutUrl;
        return;
      }

      if (!clientSecret) {
        const errorMsg = response.data?.error || response.data?.message || 'Failed to initialize payment. Please try again.';
        setMessage(errorMsg);
        setLoading(false);
        console.error('Payment intent creation failed:', response.data);
        return;
      }

      // If we have clientSecret but no checkoutUrl, use embedded form
      setClientSecret(clientSecret);
      setMessage(null);
    } catch (error) {
      const errorMessage = (error as { response?: { data?: { error?: string; message?: string } } })?.response?.data?.error ||
                          (error as { response?: { data?: { error?: string; message?: string } } })?.response?.data?.message ||
                          (error instanceof Error ? error.message : 'Failed to initialize payment');
      setMessage(errorMessage);
      console.error('Payment initialization error:', error);
    } finally {
      setLoading(false);
    }
  };

  // If we have a clientSecret, show the embedded payment form
  if (clientSecret) {
    // Create a nested Elements provider with the clientSecret
    const stripePromise = loadStripe(STRIPE_KEY);
    const appearance = { theme: 'night' as const };

    return (
      <Elements stripe={stripePromise} options={{ clientSecret, appearance }}>
        <PaymentFormContent
          formRef={formRef}
          loading={loading}
          setLoading={setLoading}
          message={message}
          setMessage={setMessage}
          items={items}
          myTeam={myTeam}
          currentLeague={currentLeague}
          user={user}
        />
      </Elements>
    );
  }

  // Otherwise, show the Pay Now button
  return (
    <Box w="100%">
      {message && (
        <Text color="red.400" mb={4}>{message}</Text>
      )}
      <Button
        id="submit"
        colorScheme="green"
        isLoading={loading}
        isDisabled={!items || items.length === 0}
        onClick={handlePayNow}
        w="175px"
        display="block"
        ml={0}
      >
        Pay now
      </Button>
    </Box>
  );
};

// Inner component that uses the Elements context
interface PaymentFormContentProps {
  formRef: React.RefObject<HTMLFormElement | null>;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  message: string | null;
  setMessage: (message: string | null) => void;
  items?: CartItem[];
  myTeam: number | null;
  currentLeague: { league_id?: number } | null;
  user: { auth_id?: string } | null;
}

const PaymentFormContent: React.FC<PaymentFormContentProps> = ({
  formRef,
  loading,
  setLoading,
  message,
  setMessage,
  items,
  myTeam,
  currentLeague,
  user,
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    if (!stripe || !elements) {
      setMessage("Stripe has not loaded yet. Please wait a moment and try again.");
      setLoading(false);
      return;
    }

    if (!items || items.length === 0) {
      setMessage("No items in cart.");
      setLoading(false);
      return;
    }

    try {
      // Build order items for success URL (similar to QuickPurchaseModal pattern)
      const orderItems = items.map((cartItem) => ({
        id: cartItem.item.id,
        amount: Math.round(cartItem.item.price * 100 * cartItem.quantity),
      }));

      const successUrl = new URL(`${window.location.origin}/payment-confirmation`);
      successUrl.searchParams.append('order_items', JSON.stringify(orderItems));
      successUrl.searchParams.append('auth_id', user?.auth_id || '');
      successUrl.searchParams.append('myTeam', myTeam?.toString() || '');
      successUrl.searchParams.append('league', currentLeague?.league_id?.toString() || '');

      const return_url = successUrl.toString();

      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url,
        },
      });

      if (error) {
        if (error.type === "card_error" || error.type === "validation_error") {
          setMessage(error.message || 'Payment failed. Please try again.');
        } else {
          setMessage(`An unexpected error occurred. ${error.message || 'Please try again.'}`);
          console.error("Stripe error:", error);
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      setMessage(errorMessage);
      console.error('Payment submission error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!stripe || !elements) {
    return (
      <Box w="100%">
        <Text color="gray.400">Loading payment form...</Text>
      </Box>
    );
  }

  return (
    <Box w="100%">
      <form id="payment-form" ref={formRef} onSubmit={handleSubmit} style={{ width: '100%' }}>
        <PaymentElement 
          options={{
            layout: "accordion"
          }}
        />
        <Button
          id="submit"
          type="submit"
          colorScheme="green"
          isLoading={loading}
          isDisabled={!stripe || !elements}
          mt={4}
          w="175px"
          display="block"
          ml={0}
        >
          Pay now
        </Button>
        {message && (
          <Text id="payment-message" color="red.400" mt={2}>
            {message}
          </Text>
        )}
      </form>
    </Box>
  );
};

export default PaymentForm; 